/**
 * CC_09 Block 7 — das EINE Teilen-Submenü als Tab-Gerüst (🔴 nicht doppelt
 * bauen). Drei Tabs:
 *   • Mail     — Freigabe via E-Mail (P8.2). 🔴 kein Existenz-Leak: die Antwort
 *                ist immer identisch, egal ob zur Adresse ein Konto existiert.
 *   • Snapshot — view-only Schnappschuss, uniform für Board/Liste/MD (D-57), hier
 *                FE-surfaced. Das uniforme Snapshot-Backend liegt in CC_10 B4;
 *                wo ein Erzeuger übergeben wird (Board), wird er genutzt, sonst
 *                zeigt der Tab den anstehenden Stand.
 *   • Live     — bewusst deaktivierter Stub (v1.8, additiv).
 *
 * Die interne Rechte-/Prinzipal-Verwaltung (Freigabe-ID) bleibt der separate
 * ShareSettingsDialog — dieses Submenü ist der externe Teilen-Weg, kein Duplikat.
 */

import { useState } from "react";

import { ShareAccessPanel } from "./ShareSettingsDialog";

import "./ryfe.scss";

export type ShareTab = "mail" | "snapshot" | "zugriff" | "live";

// Bewusst konservativ: ein Punkt vor und nach dem @, kein Whitespace. Dient nur
// der Client-Vorprüfung — die Autorität bleibt der Server.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ShareSubmenu = ({
  target,
  onClose,
  onInviteByEmail,
  onCreateSnapshot,
  wsId,
}: {
  target: { kind: "file" | "folder"; id: string; name: string };
  onClose: () => void;
  /** Optionaler Mail-Einlade-Backend-Aufruf (P8.2). Fehlt er, bleibt der Tab
   *  FE-surfaced und zeigt trotzdem die existenz-neutrale Bestätigung. */
  onInviteByEmail?: (email: string, role: string) => Promise<void>;
  /** Optionaler Snapshot-Erzeuger (Board hat einen; Liste/MD folgen in CC_10). */
  onCreateSnapshot?: () => Promise<string>;
  /** Revision R3 — mit Workspace-Kontext zeigt das Submenü den „Zugriff"-Tab:
   *  die effektive ACL-Liste (inkl. geerbter Einträge) direkt am Dokument,
   *  gerendert vom EINEN ShareAccessPanel (kein Duplikat). */
  wsId?: string;
}) => {
  const [tab, setTab] = useState<ShareTab>("mail");

  // Mail
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [mailBusy, setMailBusy] = useState(false);
  const [mailDone, setMailDone] = useState(false);
  const emailValid = EMAIL_RE.test(email.trim());

  const submitMail = async () => {
    if (!emailValid || mailBusy) {
      return;
    }
    setMailBusy(true);
    try {
      // 🔴 Existenz-neutral: ob der Aufruf 200 oder 404 liefert, darf die UI
      // nicht unterscheiden — wir schlucken Fehler und zeigen immer dieselbe
      // Bestätigung. So verrät die Freigabe nie, ob die Adresse ein Konto hat.
      if (onInviteByEmail) {
        await onInviteByEmail(email.trim(), role).catch(() => undefined);
      }
    } finally {
      setMailBusy(false);
      setMailDone(true);
      setEmail("");
    }
  };

  // Snapshot
  const [snapUrl, setSnapUrl] = useState<string | null>(null);
  const [snapBusy, setSnapBusy] = useState(false);
  const makeSnapshot = async () => {
    if (!onCreateSnapshot || snapBusy) {
      return;
    }
    setSnapBusy(true);
    try {
      setSnapUrl(await onCreateSnapshot());
    } catch {
      setSnapUrl(null);
    } finally {
      setSnapBusy(false);
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Teilen · {target.name}</h2>

        <div className="ryfe-tabs" role="tablist" aria-label="Teilen">
          <button
            role="tab"
            aria-selected={tab === "mail"}
            className={`ryfe-tab${tab === "mail" ? " ryfe-tab--active" : ""}`}
            onClick={() => setTab("mail")}
          >
            Mail
          </button>
          <button
            role="tab"
            aria-selected={tab === "snapshot"}
            className={`ryfe-tab${
              tab === "snapshot" ? " ryfe-tab--active" : ""
            }`}
            onClick={() => setTab("snapshot")}
          >
            Snapshot
          </button>
          {wsId && (
            <button
              role="tab"
              aria-selected={tab === "zugriff"}
              className={`ryfe-tab${
                tab === "zugriff" ? " ryfe-tab--active" : ""
              }`}
              onClick={() => setTab("zugriff")}
            >
              Zugriff
            </button>
          )}
          <button
            role="tab"
            aria-selected={tab === "live"}
            className="ryfe-tab"
            disabled
            title="Live-Zusammenarbeit — ab v1.8"
          >
            Live
          </button>
        </div>

        {tab === "mail" && (
          <div className="ryfe-tabpanel" role="tabpanel">
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Freigabe per E-Mail. Aus Datenschutz verrät die Bestätigung nicht,
              ob zu der Adresse ein Konto besteht.
            </p>
            <div className="ryfe-sharelist__add">
              <input
                className="ryfe-field-input"
                type="email"
                placeholder="name@example.org"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setMailDone(false);
                }}
                aria-label="E-Mail-Adresse"
              />
              <select
                className="ryfe-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                aria-label="Rolle"
              >
                <option value="viewer">Lesen</option>
                <option value="editor">Bearbeiten</option>
              </select>
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={submitMail}
                disabled={!emailValid || mailBusy}
              >
                Einladen
              </button>
            </div>
            {mailDone && (
              <div className="ryfe-muted" role="status" style={{ padding: 0 }}>
                Wenn ein Konto zu dieser Adresse besteht, wurde eine Einladung
                vorgemerkt.
              </div>
            )}
          </div>
        )}

        {tab === "snapshot" && (
          <div className="ryfe-tabpanel" role="tabpanel">
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Ein view-only Schnappschuss (eingefroren, clientseitig
              verschlüsselt) — einheitlich für Board, Liste und Notiz (D-57).
            </p>
            {onCreateSnapshot ? (
              <>
                <button
                  className="ryfe-btn ryfe-btn--primary"
                  onClick={makeSnapshot}
                  disabled={snapBusy}
                >
                  {snapBusy ? "Erzeuge…" : "Snapshot-Link erzeugen"}
                </button>
                {snapUrl && (
                  <input
                    className="ryfe-field-input"
                    readOnly
                    value={snapUrl}
                    aria-label="Snapshot-Link"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                )}
              </>
            ) : (
              <div className="ryfe-muted" style={{ padding: 0 }}>
                Der uniforme Snapshot für{" "}
                {target.kind === "file" ? "diese Datei" : "diesen Ordner"} wird
                in Kürze bereitgestellt (Backend in CC_10).
              </div>
            )}
          </div>
        )}

        {tab === "zugriff" && wsId && (
          <div className="ryfe-tabpanel" role="tabpanel">
            <ShareAccessPanel wsId={wsId} target={target} />
          </div>
        )}

        <div className="ryfe-modal__actions">
          <span className="ryfe-topbar__spacer" />
          <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};
